import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Divider, MenuItem, Select, TextField, TextButton, Typography } from 'seagull';
import { CloseCross, PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateContact } from '~/business-logic/validators';
import { useLocaleKeys } from '~/i18n';
import { UserRole } from '~/models';
import { useStoreActions } from '~/store/store-context';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from './InviteTeamStep.module.scss';
export const GetInviteTeamStepTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const inviteTeamStepTextsPath = 'vendorOnboarding.inviteTeamStep';
const validateEmail = (value, emailRequiredText, emailInvalidText)=>{
    if (value.trim().length === 0) {
        return emailRequiredText;
    }
    const result = validateContact.email(value.trim());
    if (typeof result === 'string') {
        return emailInvalidText;
    }
    return true;
};
const ROLE_OPTIONS = [
    {
        value: UserRole.Admin,
        labelKey: 'roleAdmin',
        subtextKey: 'roleAdminSubtext'
    },
    {
        value: UserRole.Member,
        labelKey: 'roleMember',
        subtextKey: 'roleMemberSubtext'
    }
];
const memberRowVariants = {
    initial: {
        opacity: 0,
        y: -8
    },
    animate: {
        opacity: 1,
        y: 0
    },
    exit: {
        opacity: 0,
        y: -8
    }
};
let nextMemberId = 1;
export const createEmptyMember = ()=>({
        id: nextMemberId++,
        firstName: '',
        lastName: '',
        email: '',
        role: ''
    });
export const InviteTeamStep = (param)=>{
    let { testId, values, onChange, onContinue, submitRef, onSubmittingChange } = param;
    const testIds = useMemo(()=>GetInviteTeamStepTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys(inviteTeamStepTextsPath);
    const { api, logBI } = useStoreActions();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const isInitialRender = useRef(true);
    useEffect(()=>{
        isInitialRender.current = false;
    }, []);
    const { control, handleSubmit, watch } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            members: values.members
        }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'members'
    });
    const watched = watch('members');
    useEffect(()=>{
        if (JSON.stringify(watched) !== JSON.stringify(values.members)) {
            onChange({
                members: watched
            });
        }
    }, [
        watched,
        values.members,
        onChange
    ]);
    useEffect(()=>{
        onSubmittingChange === null || onSubmittingChange === void 0 ? void 0 : onSubmittingChange(isSubmitting);
    }, [
        isSubmitting,
        onSubmittingChange
    ]);
    const addMember = useCallback(()=>{
        append(createEmptyMember());
    }, [
        append
    ]);
    const onValidSubmit = useCallback(async (data)=>{
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        setSubmitFailed(false);
        try {
            for (const member of data.members){
                await api.member.invite({
                    firstName: member.firstName.trim(),
                    lastName: member.lastName.trim(),
                    email: member.email.trim(),
                    role: member.role
                });
            }
            logBI(99999, {
                memberCount: data.members.length,
                action: 'continue'
            });
            onContinue();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[InviteTeamStep] invite failed', error);
            setSubmitFailed(true);
        } finally{
            setIsSubmitting(false);
        }
    }, [
        api.member,
        isSubmitting,
        logBI,
        onContinue
    ]);
    const triggerSubmit = useCallback(()=>{
        handleSubmit(onValidSubmit)();
    }, [
        handleSubmit,
        onValidSubmit
    ]);
    useImperativeHandle(submitRef, ()=>({
            submit: triggerSubmit
        }), [
        triggerSubmit
    ]);
    const showRemoveButtons = fields.length > 1;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.InviteTeamStepTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(AnimatePresence, {
                initial: false,
                children: fields.map((field, index)=>/*#__PURE__*/ _jsxs(motion.div, {
                        variants: memberRowVariants,
                        initial: isInitialRender.current ? false : 'initial',
                        animate: "animate",
                        exit: "exit",
                        transition: {
                            duration: 0.25,
                            ease: 'easeOut'
                        },
                        className: styles.memberRow,
                        "data-testid": testIds.InviteTeamStepTestIds.MemberRow(),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.nameRow,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".firstName"),
                                            rules: {
                                                required: t.firstNameRequired()
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    label: t.firstNameLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.FirstNameField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".lastName"),
                                            rules: {
                                                required: t.lastNameRequired()
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    label: t.lastNameLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.LastNameField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.contactRow,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".email"),
                                            rules: {
                                                validate: (value)=>validateEmail(value, t.emailRequired(), t.emailInvalid())
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                                    label: t.emailLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.EmailField(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.field,
                                        children: /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "members.".concat(index, ".role"),
                                            rules: {
                                                validate: (v)=>v !== '' || t.roleRequired()
                                            },
                                            render: (renderProps)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                                    label: t.roleLabel(),
                                                    collapseHelperText: true,
                                                    testId: "".concat(testIds.InviteTeamStepTestIds.RoleSelect(), "-").concat(index)
                                                }, connectControllerToTextField(renderProps)), {
                                                    children: ROLE_OPTIONS.map((param)=>{
                                                        let { value, labelKey, subtextKey } = param;
                                                        return /*#__PURE__*/ _jsxs(MenuItem, {
                                                            variant: "multiline",
                                                            value: value,
                                                            label: t[labelKey](),
                                                            testId: "".concat(testIds.InviteTeamStepTestIds.RoleSelect(), "-").concat(index, "-option-").concat(value),
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Typography, {
                                                                    weight: "normal",
                                                                    children: t[labelKey]()
                                                                }),
                                                                /*#__PURE__*/ _jsx(Typography, {
                                                                    children: t[subtextKey]()
                                                                })
                                                            ]
                                                        }, value);
                                                    })
                                                }))
                                        })
                                    })
                                ]
                            }),
                            showRemoveButtons && /*#__PURE__*/ _jsx("div", {
                                className: styles.removeButton,
                                children: /*#__PURE__*/ _jsx(TextButton, {
                                    skin: "dark",
                                    underline: "none",
                                    variant: "paragraph2",
                                    prefixIcon: /*#__PURE__*/ _jsx(CloseCross, {
                                        size: 12
                                    }),
                                    onClick: ()=>remove(index),
                                    testId: "".concat(testIds.InviteTeamStepTestIds.RemoveButton(), "-").concat(index),
                                    children: t.remove()
                                })
                            }),
                            index < fields.length - 1 && /*#__PURE__*/ _jsx(Divider, {})
                        ]
                    }, field.id))
            }),
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx(TextButton, {
                    skin: "primary",
                    underline: "none",
                    variant: "paragraph2",
                    weight: "normal",
                    prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 14
                    }),
                    onClick: addMember,
                    testId: testIds.InviteTeamStepTestIds.AddAnotherButton(),
                    children: t.addAnother()
                })
            }),
            submitFailed && /*#__PURE__*/ _jsx(Typography, {
                skin: "error",
                testId: testIds.InviteTeamStepTestIds.SubmitError(),
                children: t.submitError()
            })
        ]
    });
};
